/** The World registry write model (RFC 0261). */

import type {
  AnyDocumentId,
  DocHandle,
  DocumentId,
  Repo,
} from '@automerge/automerge-repo/slim'
import { initThread } from '../../log'
import { normalizeDocumentPath } from '../documents/mutations'
import { createPersonOwnedDocument } from '../project/creation'
import { createProject } from '../project/mutations'
import type { ProjectDoc } from '../project/types'
import type { InferenceMode, SessionDoc } from '../session/types'
import { projectWorldProjects, worldProjectMounts } from './reads'
import { writeStandingText } from './standing'
import type { SourceMove, WorldDoc, WorldSkill } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any
/**
 * The World's key space: a project doc URL, `automerge:<docId>` (RFC 0261).
 *
 * searugast keys `WorldDoc.projects` by URL, not bare id, so we do too — a
 * World written by either implementation is legible to the other. Idempotent, so
 * callers may pass an id or an already-formed URL.
 */
/**
 * The id a project lives under now: `id` itself, or where the World's move
 * map says it went (RFC 0346 3f). Follows at most three moves.
 */
export function resolveProjectMove(worldDoc: unknown, id: string): string {
  const moves = (worldDoc as { moves?: Record<string, unknown> } | undefined)
    ?.moves
  let current = id.replace(/^automerge:/, '')
  for (let hop = 0; hop < 3; hop++) {
    const to = moves?.[current]
    if (to === undefined || to === null || String(to) === current) break
    current = String(to)
  }
  return current
}

export function projectUrl(projectId: string): string {
  return projectId.startsWith('automerge:')
    ? projectId
    : `automerge:${projectId}`
}

/** A source id is unique within its project, not across the World. */
export function sourceMoveKey(projectId: string, sourceId: string): string {
  return `${projectId.replace(/^automerge:/, '')}/${encodeURIComponent(sourceId)}`
}

/** Follow every rehome a device may have missed while it was offline. */
export function resolveSourceMove(
  worldDoc: unknown,
  projectId: string,
  sourceId: string,
): SourceMove {
  const moves = (worldDoc as WorldDoc | undefined)?.sourceMoves
  let at: SourceMove = {
    projectId: projectId.replace(/^automerge:/, '') as DocumentId,
    sourceId,
  }
  const seen = new Set<string>()
  for (let hop = 0; hop < 32; hop++) {
    const key = sourceMoveKey(at.projectId, at.sourceId)
    if (seen.has(key)) throw new Error('source move cycle')
    seen.add(key)
    const next = moves?.[key]
    if (!next) return at
    at = {
      projectId: String(next.projectId).replace(
        /^automerge:/,
        '',
      ) as DocumentId,
      sourceId: String(next.sourceId),
    }
  }
  throw new Error('source moved too many times')
}

/** Keep redirects pointed at the current owner. A source can later return to
 * an old project/id; leaving that old outgoing edge in place would cycle and
 * strand devices that were offline for either move. */
export function recordSourceMove(
  world: WorldDoc,
  fromProjectId: string,
  sourceId: string,
  toProjectId: string,
  toSourceId = sourceId,
): void {
  const fromKey = sourceMoveKey(fromProjectId, sourceId)
  const toKey = sourceMoveKey(toProjectId, toSourceId)
  if (fromKey === toKey) return
  // Re-read after assigning: Automerge tracks writes through its draft proxy,
  // not through the plain object used as the assignment value.
  if (!world.sourceMoves) world.sourceMoves = {}
  const moves = world.sourceMoves
  const terminal = (start: string): string => {
    let key = start
    const seen = new Set<string>()
    for (let hop = 0; hop < 32; hop++) {
      if (seen.has(key)) throw new Error('source move cycle')
      seen.add(key)
      const next = moves[key]
      if (!next) return key
      key = sourceMoveKey(next.projectId, next.sourceId)
    }
    throw new Error('source moved too many times')
  }
  const current = terminal(fromKey)
  if (current === toKey) return // retry after an interrupted catalog transfer
  if (current !== fromKey) throw new Error('source is not at its current owner')
  const destination = terminal(toKey)
  if (destination !== toKey && destination !== fromKey)
    throw new Error('destination source identity belongs to another move')
  const ancestors = Object.keys(moves).filter(
    (key) => terminal(key) === fromKey,
  )
  if (destination === fromKey) delete moves[toKey]
  const target: SourceMove = {
    projectId: toProjectId.replace(/^automerge:/, '') as DocumentId,
    sourceId: toSourceId,
  }
  for (const key of ancestors) if (key !== toKey) moves[key] = { ...target }
  moves[fromKey] = target
}

/** Migrate a legacy World within the same transaction as its next write. */
export function ensureWorldProjects(d: Draft): Record<string, Draft> {
  if (!d.projects) {
    d.projects = {}
    for (const [key, value] of Object.entries(d.workspaces ?? {})) {
      d.projects[key] = JSON.parse(JSON.stringify(value))
    }
  }
  if (d.workspaces) delete d.workspaces
  return d.projects
}
/** Create an empty World doc (a project registry). Synchronous — `repo.create`
 *  is immediate. The caller stores `handle.documentId` in its own pointer (a
 *  file, localStorage, UserDefaults); that pointer is what makes the World
 *  reachable, and sharing it is what moves the World between devices. */
export function createWorld(repo: Repo): DocHandle<WorldDoc> {
  const home = createProject(repo, { title: 'Home' })
  const handle = repo.create<WorldDoc>()
  handle.change((d) => {
    d.projects = {}
    d.homeProjectId = home.documentId
  })
  return handle
}

/** Give a pre-0357 World its root storage project when the World is ready. */
export async function ensureWorldHomeProject(
  repo: Repo,
  worldId: string,
): Promise<string> {
  const handle = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const existing = handle.doc()?.homeProjectId
  if (existing) return existing
  const home = createProject(repo, { title: 'Home' })
  handle.change((d: Draft) => {
    if (!d.homeProjectId) d.homeProjectId = home.documentId
  })
  return handle.doc().homeProjectId as string
}
/**
 * Register a project in the World, or update the entry already there.
 *
 * `inference` defaults to `session`: a project nobody has explicitly handed to
 * the shared worker is served by whoever opened it. Defaulting to `external`
 * would invite two workers onto the same runs.
 */
export async function addProjectToWorld(
  repo: Repo,
  worldId: string,
  projectId: string,
  opts?: { displayName?: string; inference?: InferenceMode },
): Promise<void> {
  const handle = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  // A project that moved is registered under its new id, and a stale entry
  // under the old one is dropped: a window still holding the old id would
  // otherwise list the project twice (RFC 0346 3f).
  const asked = projectId.replace(/^automerge:/, '')
  const resolved = resolveProjectMove(handle.doc(), asked)
  const key = projectUrl(resolved)
  handle.change((d: Draft) => {
    const projects = ensureWorldProjects(d)
    if (resolved !== asked) delete projects[projectUrl(asked)]
    const existing = projects[key]
    if (existing) {
      // Mutate in place — re-assigning an existing doc object throws in Automerge,
      // and a field-wise update lets concurrent edits merge instead of clobbering.
      if (opts?.displayName !== undefined)
        existing.displayName = opts.displayName
      if (opts?.inference !== undefined) existing.inference = opts.inference
      return
    }
    projects[key] = {
      displayName: opts?.displayName ?? 'Project',
      inference: opts?.inference ?? 'session',
    }
  })
}
/**
 * Set who serves inference for a project (RFC 0256 placement writes here).
 *
 * No-op if the project is not registered: creating an entry as a side effect
 * of a placement decision would hide the fact that nothing registered it.
 */
export async function setProjectInference(
  repo: Repo,
  worldId: string,
  projectId: string,
  inference: InferenceMode,
): Promise<void> {
  const handle = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  const key = projectUrl(projectId)
  handle.change((d: Draft) => {
    const existing = ensureWorldProjects(d)[key]
    if (existing) existing.inference = inference
  })
}
/** Drop a project from the World. The project doc itself is untouched —
 *  this is a directory entry, not the thing itself. */
export async function removeProjectFromWorld(
  repo: Repo,
  worldId: string,
  projectId: string,
): Promise<void> {
  const handle = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  const key = projectUrl(projectId)
  handle.change((d: Draft) => {
    delete ensureWorldProjects(d)[key]
  })
}

/** Place a project at a folder in another project's visible tree. Only the
 * World edge changes: the project's documents and members stay put. */
export async function setProjectParent(
  repo: Repo,
  worldId: string,
  projectId: string,
  parentProjectId: string,
  folder: string,
): Promise<void> {
  const path = normalizeDocumentPath(folder)
  if (!path) throw new Error(`${folder} is not a project folder path`)
  const world = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  await (world as { whenReady?: () => Promise<void> }).whenReady?.()
  const snapshot = world.doc()
  const homeId = snapshot?.homeProjectId
  const projects = projectWorldProjects(snapshot)
  const ids = new Set(projects.map((project) => project.projectId))
  if (!ids.has(projectId)) throw new Error(`${projectId} is not in this World`)
  if (!homeId) throw new Error('this World has no home project yet')
  if (parentProjectId !== homeId && !ids.has(parentProjectId))
    throw new Error(`${parentProjectId} is not in this World`)

  const mounts = worldProjectMounts(snapshot)
  const byId = new Map(mounts.map((mount) => [mount.projectId, mount]))
  let ancestor: string | null = parentProjectId
  const visited = new Set<string>()
  while (ancestor && ancestor !== homeId) {
    if (ancestor === projectId || visited.has(ancestor))
      throw new Error('a project cannot be moved into itself')
    visited.add(ancestor)
    ancestor = byId.get(ancestor)?.parentProjectId ?? homeId
  }
  if (
    mounts.some(
      (mount) =>
        mount.projectId !== projectId &&
        mount.parentProjectId === parentProjectId &&
        mount.folder === path,
    )
  )
    throw new Error(`${path} already holds a project`)

  const parent = await repo.find<ProjectDoc>(parentProjectId as AnyDocumentId)
  await (parent as { whenReady?: () => Promise<void> }).whenReady?.()
  const collision = Object.values(parent.doc()?.documents ?? {}).some((ref) => {
    const existing = normalizeDocumentPath(String(ref.path))
    return existing === path || existing?.startsWith(`${path}/`)
  })
  if (collision) throw new Error(`${path} already holds documents`)
  if (
    Object.values(parent.doc()?.sources ?? {}).some((source) => {
      const folder = source.folder && normalizeDocumentPath(source.folder)
      return (
        folder &&
        (folder === path ||
          folder.startsWith(`${path}/`) ||
          path.startsWith(`${folder}/`))
      )
    })
  )
    throw new Error(`${path} overlaps a source`)

  world.change((d: Draft) => {
    const entry = ensureWorldProjects(d)[projectUrl(projectId)]
    if (!entry) throw new Error(`${projectId} left this World`)
    entry.parent = { projectId: parentProjectId, folder: path }
  })
}
/**
 * The link form for a skill's document: `automerge:<docId>` (searugast's
 * `SkillLink`). Idempotent, like `projectUrl`, so a caller may pass either.
 */
export function skillLink(documentId: string): string {
  return documentId.startsWith('automerge:')
    ? documentId
    : `automerge:${documentId}`
}
/**
 * A catalog id from a skill's title: lowercase, non-alphanumerics collapsed to
 * hyphens. STABLE ACROSS PEERS, which matters because the id is the map key: two
 * devices registering "Writing plans" must land on one entry, not two.
 *
 * Only a default. A caller with its own stable id (an installed package, an
 * imported skill) should pass that instead of deriving one from prose.
 */
export function skillIdFromTitle(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'skill'
}
/**
 * Add a skill to the World's catalog, or update the entry already there.
 *
 * The document is NOT created here — this is a directory entry pointing at a
 * document that already exists, exactly as a project entry points at a
 * project doc. `skillTools`/`mod-next skills add` create the document first
 * and then register it.
 */
export async function addWorldSkill(
  repo: Repo,
  worldId: string,
  skillId: string,
  entry: {
    title: string
    description: string
    documentId: string
    sourceUpdatedAt?: number
  },
): Promise<void> {
  const handle = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  const link = skillLink(entry.documentId)
  handle.change((d: Draft) => {
    if (!d.skills) d.skills = {}
    const existing = d.skills[skillId]
    if (existing) {
      // In place, like the project registry: re-assigning an existing doc
      // object throws in Automerge, and a field-wise update lets two peers
      // editing different fields of one skill merge instead of clobbering.
      existing.title = entry.title
      existing.description = entry.description
      existing.link = link
      if (entry.sourceUpdatedAt !== undefined)
        existing.sourceUpdatedAt = entry.sourceUpdatedAt
      return
    }
    d.skills[skillId] = {
      title: entry.title,
      description: entry.description,
      link,
      ...(entry.sourceUpdatedAt !== undefined
        ? { sourceUpdatedAt: entry.sourceUpdatedAt }
        : {}),
    } satisfies WorldSkill
  })
}
/** Drop a skill from the catalog. The DOCUMENT is untouched — un-registering a
 *  skill takes it out of every agent's prompt without destroying what you
 *  wrote, which is what makes it safe to do on a hunch. */
export async function removeWorldSkill(
  repo: Repo,
  worldId: string,
  skillId: string,
): Promise<boolean> {
  const handle = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  let existed = false
  handle.change((d: Draft) => {
    if (d.skills?.[skillId]) {
      existed = true
      delete d.skills[skillId]
    }
  })
  return existed
}
/** Set this World's AGENTS.md-style standing instructions (searugast
 *  `set_agents_md`). Replaces wholesale: it is one authored text, not a merge
 *  of many writers' opinions. */
export async function setWorldAgentsMd(
  repo: Repo,
  worldId: string,
  agentsMd: string,
): Promise<void> {
  // Into the soul file's document once it has one (RFC 0334 3g).
  await writeStandingText(repo, 'world', worldId, agentsMd)
}
/**
 * Choose which package opens a mime or a content kind (RFC 0284 S6m).
 *
 * `appId` of `''` names the BUILT-IN, which is how a person goes back after
 * trying somebody else's editor or thread. `undefined` deletes the key, which
 * is a different thing: undecided, so a single claimant wins again.
 *
 * ON THE WORLD, because it is a preference about how a person works rather than
 * something a repository owns — the same argument that put chrome here.
 */
export async function setHandler(
  repo: Repo,
  worldId: string,
  key: string,
  appId: string | undefined,
): Promise<void> {
  const handle = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  handle.change((d: Draft) => {
    if (appId === undefined) {
      if (d.handlers) delete d.handlers[key]
      return
    }
    if (!d.handlers) d.handlers = {}
    d.handlers[key] = appId
  })
}

/**
 * The World agent's session, created on first use (RFC 0336 S5).
 *
 * A session document like any other, but registered in NO project: the id is
 * kept on the World instead, so it shows in no project's list and nothing
 * scopes its tools to one project. `createdBy` is the World, the stable root a
 * session's thread is sealed against.
 */
export async function ensureWorldAgentSession(
  repo: Repo,
  worldId: string,
): Promise<string> {
  const world = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  await (world as { whenReady?: () => Promise<void> }).whenReady?.()
  const existing = worldAgentSessionId(world.doc())
  if (existing) return existing

  const now = Date.now()
  // The person's, not this device's: every device of theirs reaches the
  // World agent (RFC 0345).
  const session = await createPersonOwnedDocument<SessionDoc>(repo)
  const sessionId = session.documentId as string
  session.change((d) => {
    d.id = sessionId
    d.title = 'Mod'
    d.createdAt = now
    d.updatedAt = now
    d.status = 'active'
    d.createdBy = worldId
    initThread(d as unknown as Record<string, unknown>)
  })
  world.change((d: Draft) => {
    // A key of its own at the top of the World, never inside a map that may
    // not exist yet: two devices each creating the map would conflict and
    // one map would be lost. Distinct keys always merge, and the earliest is
    // read as the agent everywhere.
    d[`${AGENT_SESSION_KEY}${sessionId}`] = now
  })
  return worldAgentSessionId(world.doc()) ?? sessionId
}

/** A World agent candidate's key on the World: the prefix, then its id. */
const AGENT_SESSION_KEY = 'agentSession:'

/** The World agent's session id, or null before anyone has created it: the
 *  legacy pointer when there is one, else the earliest session made. */
export function worldAgentSessionId(worldDoc: unknown): string | null {
  const doc = worldDoc as WorldDoc | undefined
  if (doc?.agentSessionId) return String(doc.agentSessionId)
  const candidates: Record<string, unknown> = { ...(doc?.agentSessions ?? {}) }
  for (const [key, at] of Object.entries(doc ?? {})) {
    if (key.startsWith(AGENT_SESSION_KEY))
      candidates[key.slice(AGENT_SESSION_KEY.length)] = at
  }
  let best: { id: string; at: number } | null = null
  for (const [id, raw] of Object.entries(candidates)) {
    const at = Number(raw)
    if (!best || at < best.at || (at === best.at && id < best.id))
      best = { id, at }
  }
  return best?.id ?? null
}
